import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';

export const Input = forwardRef(({
  label,
  error,
  helper,
  icon: Icon,
  type = 'text',
  className = '',
  containerClassName = '',
  id,
  name,
  ...props
}, ref) => {
  // Generate unique ID for label association
  const inputId = id || `input-${name || Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <div className={`space-y-2 ${containerClassName}`}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-zinc-300">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <motion.input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          className={`
            w-full px-4 py-3 
            ${Icon ? 'pl-12' : ''}
            bg-zinc-900/50 
            border border-zinc-800 
            rounded-xl
            text-zinc-100 
            placeholder-zinc-500
            transition-all duration-300 ease-out
            focus:outline-none 
            focus:ring-2 focus:ring-emerald-500/50 
            focus:border-emerald-500
            focus:shadow-[0_0_20px_rgba(16,185,129,0.15)]
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
            ${className}
          `}
          whileFocus={{ scale: 1.01 }}
          {...props}
        />
      </div>
      {error && (
        <p className="text-sm text-red-400 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
      {helper && !error && (
        <p className="text-sm text-zinc-500">{helper}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export const Textarea = forwardRef(({
  label,
  error,
  helper,
  className = '',
  containerClassName = '',
  rows = 4,
  id,
  name,
  ...props
}, ref) => {
  // Generate unique ID for label association
  const textareaId = id || `textarea-${name || Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <div className={`space-y-2 ${containerClassName}`}>
      {label && (
        <label htmlFor={textareaId} className="block text-sm font-medium text-zinc-300">
          {label}
        </label>
      )}
      <motion.textarea
        ref={ref}
        id={textareaId}
        name={name}
        rows={rows}
        className={`
          w-full px-4 py-3 
          bg-zinc-900/50 
          border border-zinc-800 
          rounded-xl
          text-zinc-100 
          placeholder-zinc-500
          resize-none
          transition-all duration-300 ease-out
          focus:outline-none 
          focus:ring-2 focus:ring-emerald-500/50 
          focus:border-emerald-500
          focus:shadow-[0_0_20px_rgba(16,185,129,0.15)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
          ${className}
        `}
        whileFocus={{ scale: 1.005 }}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-400 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
      {helper && !error && (
        <p className="text-sm text-zinc-500">{helper}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Input;
